import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from 'components/elements/Form/Input/input';
import { useQuery } from 'react-query';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import useSaveApplessSensor from './crud/useSave';
import Modal from 'components/elements/Modal';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import { toast } from 'react-toastify';
import {
  generateApiFailErrorMessage,
  generateSaveErrorMessage,
  generateSaveSuccessMessage,
  generateUpdateErrorMessage,
  generateUpdateSuccessMessage
} from 'utils/helpers';
import SelectInput from 'components/elements/Form/select/selectInput';
import { ApplessOccupancySensorTypes, GetNonServerErrorMessage } from 'utils/constants';
import { ApplessSensorProps } from 'services/hooks/appless-occupancy-sensor-service';
import SpaceService, { SpaceProps } from 'services/hooks/space-service';
import { SelectOption } from 'utils/types';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import CheckboxInput from 'components/elements/Form/Input/CheckboxInput';
import SensorsIcon from 'assets/Icons/sidebarIcons/SensorsIcon';
import { HelpTooltipIcon } from 'components/elements/Help/HelpTooltipIcon';

type CreateProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditUUID?: React.Dispatch<React.SetStateAction<string>>;
  createQuery: any;
  updateQuery: any;
  editQuery?: any;
  editUUID?: string;
  queryName: string;
};

// Validation
const CreateApplessSensorSchema = yup.object().shape({
  sensorId: yup.string().required('Sensor ID is required'),
  buildingUuid: yup.string().required('Building is required'),
  location: yup.string().required('Location is required'),
  type: yup.string().required('Sensor Type is required'),
  reversed: yup.boolean()
});

export default function Save({
  entityName,
  showModal,
  setEditUUID,
  setShowModal,
  createQuery,
  updateQuery,
  editQuery,
  editUUID,
  queryName
}: CreateProps) {
  const [applessSensorRecord, setApplessSensorRecord] = useState<ApplessSensorProps | null>(null);
  const [spaceOptions, setSpaceOptions] = useState<SelectOption[]>([]);
  const [buildingOptions, setBuildingOptions] = useState<SelectOption[]>([]);
  const [disabled, setDisabled] = useState(false);

  const { isSuccess, data } = useQuery<ApplessSensorProps, Error>(
    ['applessOccupancySensor', editUUID],
    () => editQuery(editUUID),
    {
      enabled: !!editUUID,
      onError: () => {
        toast.error(generateApiFailErrorMessage('App-less occupancy sensor'));
      }
    }
  );

  const { data: buildingsData } = useQuery<BuildingProps[], Error>(
    'buildings',
    BuildingService.findAllBuildings,
    {
      onError: () => {
        toast.error(generateApiFailErrorMessage('Buildings'));
      }
    }
  );

  const { data: spacesData } = useQuery<SpaceProps[], Error>('spaces', SpaceService.findAllSpaces, {
    onError: () => {
      toast.error(generateApiFailErrorMessage('Spaces'));
    }
  });

  const methods = useForm({
    defaultValues: applessSensorRecord
      ? {
          uuid: applessSensorRecord?.uuid,
          sensorId: applessSensorRecord?.sensorId,
          buildingUuid: applessSensorRecord?.buildingUuid,
          spaceUuid: applessSensorRecord?.spaceUuid,
          reversed: applessSensorRecord?.reversed,
          location: applessSensorRecord?.location,
          type: applessSensorRecord?.type
        }
      : {
          uuid: '',
          sensorId: '',
          buildingUuid: '',
          spaceUuid: '',
          reversed: false,
          location: '',
          type: ''
        },
    resolver: yupResolver(CreateApplessSensorSchema)
  });

  useEffect(() => {
    if (data && editUUID) {
      setApplessSensorRecord(data);
      methods.reset(data);
    }
  }, [data, editUUID, methods]);

  // Set building options
  useEffect(() => {
    if (buildingsData) {
      setBuildingOptions(
        buildingsData.map((building) => ({
          value: building.uuid as string,
          label: building.name
        }))
      );
    }
  }, [buildingsData]);

  const buildingWatch = methods.watch('buildingUuid');
  const spaceWatch = methods.watch('spaceUuid');

  // Filter space options if building is selected
  useEffect(() => {
    if (spacesData) {
      if (buildingWatch) {
        const filteredSpaces = spacesData.filter(
          (space) => space.buildingUuid === buildingWatch && space.uuid !== undefined
        );
        setSpaceOptions(
          filteredSpaces.map((space) => ({
            value: space.uuid as string,
            label: space.name
          }))
        );
      } else {
        const validSpaces = spacesData.filter((space) => space.uuid !== undefined);
        setSpaceOptions(
          validSpaces.map((space) => ({
            value: space.uuid as string,
            label: space.name
          }))
        );
      }
    }
  }, [spacesData, buildingWatch]);

  // Reset space when the selected building changes and the space does not belong to that building.
  useEffect(() => {
    if (spacesData && buildingWatch) {
      const selectedSpace = spacesData.find((space) => space.uuid === spaceWatch);
      if (selectedSpace && selectedSpace.buildingUuid !== buildingWatch) {
        methods.setValue('spaceUuid', '');
      }
    }
  }, [spacesData, buildingWatch, spaceWatch, methods]);

  const saveApplessSensorMutation = useSaveApplessSensor({
    createQuery,
    updateQuery,
    queryName,
    onSuccess: () => {
      setShowModal(false);
      setDisabled(false);
      if (!editUUID) {
        toast.success(generateSaveSuccessMessage('App-less occupancy sensor'));
      } else {
        toast.success(generateUpdateSuccessMessage('App-less occupancy sensor'));
      }
      if (setEditUUID) setEditUUID('');
    },
    onError: (error: any) => {
      setDisabled(false);
      if (error.response) {
        if (!editUUID) {
          toast.error(generateSaveErrorMessage('App-less occupancy sensor'));
        } else {
          toast.error(generateUpdateErrorMessage('App-less occupancy sensor'));
        }
      } else {
        // Handle cases where the error is not from the server response
        toast.error(GetNonServerErrorMessage);
      }
    }
  });

  const onSubmit = async (e: ApplessSensorProps) => {
    setDisabled(true);

    saveApplessSensorMutation.mutate({
      uuid: e?.uuid,
      sensorId: e?.sensorId,
      buildingUuid: e?.buildingUuid,
      spaceUuid: e?.spaceUuid ? e?.spaceUuid : undefined,
      reversed: e?.reversed,
      location: e?.location,
      type: e?.type
    });
  };

  return !editUUID || isSuccess ? (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          if (setEditUUID) setEditUUID('');
        }}
        title={entityName}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Choose a Building*"
              placeholder="Select Building"
              name="buildingUuid"
              errors={methods.formState.errors}
              disabled={false}
              options={buildingOptions}
            />
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Choose a Space"
              placeholder="Select Space"
              name="spaceUuid"
              errors={methods.formState.errors}
              disabled={false}
              options={spaceOptions}
            />
            <Input
              label="Sensor ID*"
              name="sensorId"
              type="text"
              placeholder="Enter Sensor ID"
              methods={methods}
              autoFocus
            />
            <CheckboxInput
              label="Is Sensor Reversed?"
              name="reversed"
              placeholder="Enter Reversed"
              methods={methods}
              helpTooltipMessage="Check this box if the sensor is installed facing the opposite direction"
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}
            >
              <div style={{ flexGrow: 1 }}>
                <Input
                  label="Location*"
                  name="location"
                  type="text"
                  placeholder="Enter Location"
                  methods={methods}
                />
              </div>
              <HelpTooltipIcon message="Enter the location where the sensor is installed for more precise identification. This is useful when the space name alone does not provide enough detail, such as 'building front entrance' or 'conference room entrance." />
            </div>
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Sensor Type*"
              name="type"
              errors={methods.formState.errors}
              placeholder="Select a sensor type"
              options={ApplessOccupancySensorTypes}
            />
          </div>
          <PrimaryButton
            buttonLabel={entityName}
            iconLeft={<SensorsIcon />}
            variant="default"
            size={'mt-4 px-6 w-auto gap-2'}
            disabled={disabled}
          />
        </form>
      </Modal>
    </>
  ) : (
    <LoadingSpinner />
  );
}
